import {
	getFormProps,
	getInputProps,
	getSelectProps,
	getTextareaProps,
	useForm,
} from '@conform-to/react'
import { getZodConstraint, parseWithZod } from '@conform-to/zod'
import * as E from '@react-email/components'
import { type ActionFunctionArgs } from '@remix-run/node'
import { Form, json } from '@remix-run/react'
import { countries, type TCountryCode } from 'countries-list'
import { HoneypotInputs } from 'remix-utils/honeypot/react'
import { z } from 'zod'
import { Field, SelectField, TextareaField } from '#app/components/forms.js'
import { Separator } from '#app/components/ui/separator.js'
import { StatusButton } from '#app/components/ui/status-button.js'
import { prisma } from '#app/utils/db.server.js'
import { sendEmail } from '#app/utils/email.server.js'
import { checkHoneypot } from '#app/utils/honeypot.server.js'
import { useIsPending } from '#app/utils/misc.js'
import { redirectWithToast } from '#app/utils/toast.server.js'
import { EmailSchema } from '#app/utils/user-validation.js'

const ContributorApplicationSchema = z.object({
	name: z.string().trim(),
	email: EmailSchema,
	biography: z.string().trim(),
	location: z.string().trim(),
	links: z.string().trim(),
	howDidYouHearAboutUs: z.string().trim(),
})

export async function action({ request }: ActionFunctionArgs) {
	const formData = await request.formData()
	checkHoneypot(formData)
	const submission = await parseWithZod(formData, {
		schema: ContributorApplicationSchema,
		async: true,
	})
	if (submission.status !== 'success') {
		return json(
			{ result: submission.reply() },
			{ status: submission.status === 'error' ? 400 : 200 },
		)
	}
	await prisma.contributorApplication.create({
		data: {
			...submission.value,
		},
	})
	await sendEmail({
		to: submission.value.email,
		subject: `Kogia | Contributor Application Received`,
		react: <ContributorApplicationEmail name={submission.value.name} />,
	})
	return redirectWithToast('/', {
		title: 'Thank you!',
		description: 'We have received your application. We will be in touch',
	})
}

export function ContributorApplicationEmail({ name }: { name: string }) {
	return (
		<E.Html lang="en" dir="ltr">
			<E.Head />
			<E.Preview>Thank you for your application!</E.Preview>
			<E.Body>
				<E.Container>
					<E.Heading as="h1">Thank you for your application!</E.Heading>
					<E.Text>Dear {name},</E.Text>
					<E.Text>
						Thank you for your application, and for considering donating your
						remarkable work to Kogia's Oceanic Library!
					</E.Text>
					<E.Text>
						We will soon be reviewing your application, and we will get back to
						you as soon as possible. Once again, thanks again for your
						commitment to our shared mission.
					</E.Text>
					<E.Text>
						If you have any questions or wish to provide more details, please
						don't hesitate to reach us at{' '}
						<E.Link href={'mailto:info@kogia.org'}>info@kogia.org</E.Link>.
					</E.Text>
					<E.Text>All the best,</E.Text>
					<E.Text>The Kogia Team</E.Text>
				</E.Container>
			</E.Body>
		</E.Html>
	)
}

export default function MembershipApply() {
	const isPending = useIsPending({
		formAction: `/contributor/apply`,
		formMethod: 'POST',
	})
	const [form, fields] = useForm({
		id: 'contributor-membership-apply',
		constraint: getZodConstraint(ContributorApplicationSchema),
		// lastResult: actionData?.result,
		onValidate({ formData }) {
			return parseWithZod(formData, { schema: ContributorApplicationSchema })
		},
		shouldRevalidate: 'onBlur',
		// shouldValidate: 'onInput',
	})
	return (
		<div className="flex w-full max-w-screen-md flex-col gap-3 p-4 md:p-8">
			<div className="flex items-center gap-4">
				<h2 className="font-serif text-h2">Request Contributor Access</h2>
				<Separator className="flex-1" />
			</div>
			<Form
				action={`/contributor/apply`}
				method="POST"
				{...getFormProps(form)}
				className="w-full"
			>
				<HoneypotInputs />
				<Field
					className="flex-1"
					labelProps={{
						htmlFor: fields.name.id,
						children: 'Name',
					}}
					inputProps={{
						...getInputProps(fields.name, { type: 'text' }),
						name: 'name',
					}}
					errors={fields.name.errors}
				/>
				<Field
					labelProps={{
						htmlFor: fields.email.id,
						children: 'Email',
					}}
					inputProps={{
						...getInputProps(fields.email, { type: 'email' }),
						name: 'email',
					}}
					errors={fields.email.errors}
				/>
				<TextareaField
					labelProps={{
						htmlFor: fields.biography.id,
						children: 'Biography',
					}}
					textareaProps={{
						...getTextareaProps(fields.biography),
						name: 'biography',
					}}
					errors={fields.biography.errors}
				/>
				<SelectField
					labelProps={{
						htmlFor: fields.location.id,
						children: 'Where are you based?',
					}}
					selectProps={{ ...getSelectProps(fields.location) }}
					mySelectProps={{
						name: 'location',
						isMulti: false,
						isSearchable: true,
						defaultOptions: [],
						options: Object.keys(countries).map(code => {
							const val = countries[code as TCountryCode].name
							return { label: val, value: val }
						}),
						singularItem: 'country',
						onChange: () => form.validate({ name: 'location' }),
						placeholder: 'Select country',
					}}
					errors={fields.location.errors}
				/>
				{/* <Field
					labelProps={{
						htmlFor: fields.location.id,
						children: 'Location',
					}}
					inputProps={{
						...getInputProps(fields.location, { type: 'text' }),
						name: 'location',
					}}
					errors={fields.location.errors}
				/> */}
				<TextareaField
					labelProps={{
						htmlFor: fields.links.id,
						children: 'Links',
					}}
					textareaProps={{
						...getTextareaProps(fields.links),
						name: 'links',
					}}
					errors={fields.links.errors}
				/>
				<TextareaField
					labelProps={{
						htmlFor: fields.howDidYouHearAboutUs.id,
						children: 'How did you hear about us?',
					}}
					textareaProps={{
						...getTextareaProps(fields.howDidYouHearAboutUs),
						name: 'howDidYouHearAboutUs',
					}}
					errors={fields.howDidYouHearAboutUs.errors}
				/>
				<div className="mt-4 flex items-center justify-between gap-6">
					<StatusButton
						className="mt-4 w-full"
						status={isPending ? 'pending' : form.status ?? 'idle'}
						type="submit"
						disabled={isPending}
					>
						Apply
					</StatusButton>
				</div>
			</Form>
		</div>
	)
}
